/**
 * Protected Route Component
 *
 * Redirects to login if user is not authenticated
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireRole?: 'admin' | 'analyst' | 'viewer';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole
}) => {
  const { user, userProfile, loading, isMfaEnrolled } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" role="status" aria-label="Loading your workspace">
        <div className="text-center">
          <div className="motion-safe:animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" aria-hidden="true"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (userProfile?.status === 'invited') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" role="alert">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Invitation Pending</h2>
          <p className="text-muted-foreground">
            Finish setup from your invitation email before accessing the platform.
          </p>
        </div>
      </div>
    );
  }

  // Enforce email verification
  if (!user.emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Enforce MFA enrollment
  // TEMPORARY: Disabled to unblock user while configuring Firebase Console
  // if (!isMfaEnrolled) {
  //   return <Navigate to="/setup-mfa" replace />;
  // }

  // Check role if required
  if (requireRole && userProfile) {
    const roleHierarchy = { viewer: 1, analyst: 2, admin: 3 };
    if (roleHierarchy[userProfile.role] < roleHierarchy[requireRole]) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4" role="alert">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to view this page.</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
